import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useWorkspaceId } from "../bridges/session";
import { api } from "../data/client";
import { scopedKey } from "../data/keys";

export type StartHere = { people: boolean; route: boolean; report: boolean; team: boolean };
export type HelpState = { items: ReadonlySet<string>; topics: ReadonlySet<string>; startHere: StartHere };

type Wire = { seen?: { items?: string[]; topics?: string[] }; start_here?: Partial<StartHere> };
const map = (w: Wire): HelpState => ({
  items: new Set(w.seen?.items ?? []),
  topics: new Set(w.seen?.topics ?? []),
  startHere: { people: !!w.start_here?.people, route: !!w.start_here?.route, report: !!w.start_here?.report, team: !!w.start_here?.team },
});

const stateKey = (ws: number | null) => scopedKey(ws, "help", "state");

/** What this person has opened in Help and the Start-here facts of the account. `enabled` false: no request (a locked account, or today's Help). */
export const useHelpState = (enabled = true) => {
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: stateKey(ws),
    queryFn: async () => map((await api.get<{ data: Wire }>("/api/auth/next/help/state")).data),
    staleTime: 60_000,
    enabled: ws !== null && enabled,
  });
};

/** Marks a topic or an item opened: at once on screen, then on the server. A failed call is forgotten quietly (the label comes back next time), never an error. */
export const useMarkSeen = () => {
  const ws = useWorkspaceId();
  const client = useQueryClient();
  const m = useMutation({
    mutationFn: (v: { kind: "item" | "topic"; id: string }) => api.post("/api/auth/next/help/seen", v, { quietServerError: true }),
    onMutate: (v) => {
      client.setQueryData<HelpState>(stateKey(ws), (old: HelpState | undefined) => {
        if (!old) return old;
        const bucket = v.kind === "item" ? "items" : "topics";
        if (old[bucket].has(v.id)) return old;
        return { ...old, [bucket]: new Set(Array.from(old[bucket]).concat(v.id)) };
      });
    },
    onError: () => { void client.invalidateQueries({ queryKey: stateKey(ws) }); },
  });
  return (kind: "item" | "topic", id: string) => m.mutate({ kind, id });
};
